import type { ContentTypeName } from "./core"

export type ContentFieldKind =
  | "string"
  | "text"
  | "markdown"
  | "date"
  | "locale"
  | "year"
  | "careerTeam"
  | "boolean"
  | "url"
  | "image"
  | "imageArray"
  | "stringArray"

export type ContentFieldDefinition = {
  name: string
  label: string
  kind: ContentFieldKind
  required?: boolean
  readOnly?: boolean
  group?: string
  description?: string
}

export type ContentTypeDefinition = {
  type: ContentTypeName
  label: string
  description: string
  fields: ContentFieldDefinition[]
}

const commonSeoFields: ContentFieldDefinition[] = [
  {
    name: "metaDescription",
    label: "Meta Description",
    kind: "text",
    group: "SEO",
    description: "Optional search-result description. Leave blank to use the page default.",
  },
  {
    name: "metaOgTitle",
    label: "Meta og:title",
    kind: "string",
    group: "SEO",
    description: "Optional social-share title. Leave blank to reuse the record title.",
  },
  {
    name: "metaOgDescription",
    label: "Meta og:description",
    kind: "text",
    group: "SEO",
    description: "Optional social-share description. Leave blank to reuse the meta description.",
  },
]

export const contentTypeDefinitions: ContentTypeDefinition[] = [
  {
    type: "news",
    label: "News",
    description: "News articles and detail-page metadata.",
    fields: [
      field("language", "Language", "locale", "Publishing", "Which locale this record publishes to.", true),
      field(
        "publishDate",
        "Publish date",
        "date",
        "Publishing",
        "Published records use this date for ordering and metadata."
      ),
      field("title", "Title", "string", "Identity", "Public headline and dashboard record name.", true),
      field(
        "slug",
        "Slug",
        "string",
        "Identity",
        "URL-safe path segment for the public news detail page.",
        true
      ),
      field(
        "newsCoverBlurb",
        "News Cover Blurb",
        "text",
        "Content",
        "Short teaser shown on news listing cards.",
        true
      ),
      field(
        "bio",
        "Content Markdown",
        "markdown",
        "Content",
        "Markdown body rendered on the public news detail page.",
        true
      ),
      field(
        "newsCoverImage",
        "News Cover Image",
        "image",
        "Media",
        "Primary image for news cards and the detail page.",
        true
      ),
      ...commonSeoFields,
    ],
  },
  {
    type: "careers",
    label: "Careers",
    description: "Job listings and career detail pages.",
    fields: [
      field(
        "language",
        "Language",
        "locale",
        "Publishing",
        "Which locale this job listing publishes to.",
        true
      ),
      field(
        "active",
        "Active",
        "boolean",
        "Publishing",
        "Controls whether this listing is shown as open on the public site.",
        true
      ),
      field(
        "publishDate",
        "Publish date",
        "date",
        "Publishing",
        "Published records use this date for ordering and metadata."
      ),
      field(
        "title",
        "Position Name",
        "string",
        "Identity",
        "Public job title and dashboard record name.",
        true
      ),
      field(
        "teamKey",
        "Team",
        "careerTeam",
        "Identity",
        "Reusable team label for filtering and grouping career listings.",
        true
      ),
      field(
        "slug",
        "Slug",
        "string",
        "Identity",
        "URL-safe path segment for the public career detail page.",
        true
      ),
      field(
        "newsBlurb",
        "Career Blurb",
        "text",
        "Content",
        "Short teaser shown on career listing cards.",
        true
      ),
      field(
        "description",
        "Content Markdown",
        "markdown",
        "Content",
        "Markdown body rendered on the public career detail page.",
        true
      ),
      field(
        "newsCoverImage",
        "Career Cover Image",
        "image",
        "Media",
        "Primary image for career cards and the detail page.",
        true
      ),
      ...commonSeoFields,
    ],
  },
  {
    type: "workProducts",
    label: "Work Products",
    description: "Product entries shown under the work page products section.",
    fields: [
      field(
        "language",
        "Language",
        "locale",
        "Publishing",
        "Which locale this product entry publishes to.",
        true
      ),
      field("title", "Title", "string", "Identity", "Product name shown on the public work page.", true),
      field("url", "URL", "url", "Identity", "Destination opened when visitors select this product.", true),
      field(
        "coverImage",
        "Cover Image",
        "image",
        "Media",
        "Image shown in the product area of the work page.",
        true
      ),
    ],
  },
  {
    type: "workMarketing",
    label: "Work Marketing",
    description: "Marketing and production work records shown in gallery sections.",
    fields: [
      field(
        "language",
        "Language",
        "locale",
        "Publishing",
        "Which locale this work record publishes to.",
        true
      ),
      field("title", "Title", "string", "Identity", "Work record title shown in gallery contexts.", true),
      field(
        "coverImage",
        "Cover Image",
        "image",
        "Media",
        "Primary image shown before visitors open the gallery.",
        true
      ),
      field(
        "gallery",
        "Gallery",
        "imageArray",
        "Media",
        "Ordered images shown for this marketing or production work record.",
        true
      ),
    ],
  },
  {
    type: "workTags",
    label: "Work Tags",
    description: "Localized tag lists shown across the work page sections.",
    fields: [
      field("language", "Language", "locale", "Publishing", "Which locale this tag set publishes to.", true),
      field("title", "Title", "string", "Identity", "Internal name for this localized tag set.", true),
      {
        name: "esportsBroadcastTags",
        label: "Esports & Broadcast Tags",
        kind: "stringArray",
        group: "Tags",
        description: "Tags shown for esports and broadcast work.",
      },
      {
        name: "gameCultureMarketingTags",
        label: "Game Culture Marketing Tags",
        kind: "stringArray",
        group: "Tags",
        description: "Tags shown for game culture and marketing work.",
      },
      field("productTags", "Product Tags", "stringArray", "Tags", "Tags shown for product work."),
    ],
  },
  {
    type: "companyHistory",
    label: "Company History",
    description: "Timeline entries and supporting images for the company page.",
    fields: [
      field(
        "language",
        "Language",
        "locale",
        "Publishing",
        "Which locale this timeline entry publishes to.",
        true
      ),
      field(
        "title",
        "Title",
        "string",
        "Identity",
        "Timeline entry heading shown on the company page.",
        true
      ),
      field("year", "Year", "year", "Identity", "Year used to order and label the timeline entry.", true),
      field(
        "description",
        "Description",
        "text",
        "Content",
        "Short description shown inside the company timeline entry.",
        true
      ),
      field("images", "Images", "imageArray", "Media", "Optional supporting images for this timeline entry."),
    ],
  },
]

function field(
  name: string,
  label: string,
  kind: ContentFieldKind,
  group: string,
  description: string,
  required?: boolean
): ContentFieldDefinition {
  return { name, label, kind, group, description, required }
}

export function getContentTypeDefinition(type: ContentTypeName) {
  return contentTypeDefinitions.find((definition) => definition.type === type) ?? contentTypeDefinitions[0]
}
